import { FormattedMessage, useIntl } from "react-intl";

import Button from "@components/buttons/Button";
import TwoFactorCodeForm from "@components/forms/TwoFactorCodeForm";
import IconBase from "@components/text/IconBase";
import TextWithIcon from "@components/text/TextWithIcon";
import styles from "@components/TwoFactorAuthentication.module.css";
import BodyCopy from "@components/typography/BodyCopy";
import useTwoFactorAuthenticator from "@hooks/useTwoFactorAuthenticator";
import { ClientFragment, PhoneNumberChannelEnum } from "@typing/Generated";

type Props = {
  client: Pick<ClientFragment, "id" | "verificationPhoneNumber" | "twoFactorType"> | null;
};

const TwoFactorAuthentication = ({ client }: Props) => {
  const intl = useIntl();
  const twoFactorAuthenticator = useTwoFactorAuthenticator({ client, settingUp: false });

  if (!client) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div>
        <BodyCopy bold color="gray">
          <FormattedMessage id="pages.client.account.twoFactor.enterCode" />
        </BodyCopy>
        <div className="ion-margin-top">
          <FormattedMessage
            id={
              client.verificationPhoneNumber
                ? "pages.client.account.twoFactor.SMS.inputCode"
                : "pages.client.account.twoFactor.TOTP.inputCode"
            }
            values={{ number: client.verificationPhoneNumber?.formattedNumber }}
          />
        </div>
        <TwoFactorCodeForm onSuccess={twoFactorAuthenticator.handleVerificationSuccess} />
        {twoFactorAuthenticator.showLoader ? (
          <TextWithIcon
            icon="spinner"
            iconFirst
            text={intl.formatMessage({ id: "pages.client.account.twoFactor.wait" })}
          />
        ) : client.verificationPhoneNumber ? (
          <>
            <div className={styles.spacing}>
              <FormattedMessage id="pages.client.account.twoFactor.noCode" />
            </div>
            <div className="flex justify-center items-center">
              <div className="ion-margin-end">
                <Button
                  disabled={twoFactorAuthenticator.resendDisabled}
                  fill="clear"
                  onClick={() => {
                    twoFactorAuthenticator.resendCode(PhoneNumberChannelEnum.SMS);
                  }}
                >
                  <IconBase icon="repeat" slot="start" />
                  <FormattedMessage id="pages.client.account.twoFactor.resendCode" />
                </Button>
              </div>
              <div>
                <Button
                  disabled={twoFactorAuthenticator.resendDisabled}
                  fill="clear"
                  onClick={() => {
                    twoFactorAuthenticator.resendCode(PhoneNumberChannelEnum.CALL);
                  }}
                >
                  <IconBase icon="phone" slot="start" />
                  <FormattedMessage id="pages.client.account.twoFactor.receiveCall" />
                </Button>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default TwoFactorAuthentication;
